import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  LoaderCircle,
  Trash2,
} from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";

import {
  analyzeDatabookSheetDeletion,
  applyDatabookSheetDeletion,
  type DatabookSheetDeletionImpact,
  type DatabookSheetListItem,
  type DatabookSheetPreview,
  type DatabookOutcome,
  type DatabookInsights,
} from "@/lib/api/databook";
import { BackendError } from "@/lib/backend";
import { backendErrorMessage } from "@/lib/backendErrorMessage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";

import {
  BuilderReportRail,
  BuilderReportRailItem,
  BuilderReportRailSearch,
} from "./BuilderReportRail";
import { DatabookInsightsInspector } from "./DatabookInsightsInspector";
import { DatabookSheetGrid } from "./DatabookSheetGrid";
import type { DatabookCellSelection } from "./databookCellSelection";
import {
  nextDatabookSheetPage,
  previousDatabookSheetPage,
} from "./databookPreviewState";

export type DatabookGenerationState = { outcome?: DatabookOutcome } & (
  | { status: "checking" }
  | { status: "reconnecting" }
  | { status: "not_started" }
  | { status: "finalizing" }
  | { status: "queued" }
  | {
      status: "running";
      completedSteps: number;
      totalSteps: number;
    }
  | { status: "ready" }
  | { status: "start_failed" }
  | { status: "failed" });

interface DatabookGenerationStageProps {
  toolbarActions?: ReactElement;
  insights?: DatabookInsights;
  projectId?: string;
  previewRevision?: number;
  versionId?: string;
  onRetryWorkbook?: () => void;
  state: DatabookGenerationState;
  versionOutcome?: DatabookOutcome;
  sheetNames: string[] | null;
  sheetItems: DatabookSheetListItem[] | null;
  alertSheetNames: string[];
  sheetListError: boolean;
  selectedSheetName: string | null;
  sheetPreview: DatabookSheetPreview | null;
  sheetPreviewLoading: boolean;
  sheetPreviewFetching: boolean;
  sheetPreviewError: boolean;
  selection?: DatabookCellSelection | null;
  canEdit: boolean;
  canRetry?: boolean;
  onRetry: () => void;
  onBack: () => void;
  onSelectSheet: (sheetName: string) => void;
  onSelectSheetPage: (rowOffset: number) => void;
  onToggleOutlineGroup: (groupId: string) => void;
  onToggleColumnOutlineGroup?: (groupId: string) => void;
  onRetrySheet: () => void;
  onSelectionChange?: (selection: DatabookCellSelection | null) => void;
  onDeletionApplied?: () => Promise<void>;
}

const MODEL_FAILURE_MESSAGE = "Some analysis could not be completed because the AI model was temporarily unavailable. Please retry.";

const DATABOOK_FAILURE_MESSAGE = "Something went wrong. Please allow up to 24 hours for your databook to be ready.";

const ignoreSelectionChange = () => undefined;
const ignoreOutlineToggle = () => undefined;

function progressPercent(state: DatabookGenerationState): number {
  if (state.status === "ready") return 100;
  if (state.status !== "running" || state.totalSteps === 0) return 0;
  return Math.min(
    99,
    Math.round((state.completedSteps / state.totalSteps) * 100),
  );
}

function dividerLabel(sheetName: string): string {
  return sheetName.replace(/(?:->|>>)$/, "").trim();
}

function formatSheetSize(sizeBytes: number): string {
  const megabytes = sizeBytes / (1024 * 1024);
  if (megabytes < 0.01) return "<0.01 MB";
  return `${megabytes.toFixed(megabytes < 1 ? 2 : 1)} MB`;
}

interface SheetNavigationGroup {
  id: string;
  title: string | null;
  items: DatabookSheetListItem[];
  subgroups: SheetNavigationGroup[];
  sizeBytes?: number;
}

function groupSheetItems(
  sheetItems: DatabookSheetListItem[],
): SheetNavigationGroup[] {
  const groups: SheetNavigationGroup[] = [];
  let currentGroup: SheetNavigationGroup | null = null;
  let sourcesGroup: SheetNavigationGroup | null = null;

  for (const item of sheetItems) {
    if (item.selectable) {
      if (currentGroup === null) {
        currentGroup = {
          id: "ungrouped",
          title: null,
          items: [],
          subgroups: [],
        };
        groups.push(currentGroup);
      }
      currentGroup.items.push(item);
      continue;
    }

    const title = dividerLabel(item.name);
    const group = { id: item.name, title, items: [], subgroups: [] };
    if (title.toLowerCase() === "sources") {
      groups.push(group);
      sourcesGroup = group;
    } else if (sourcesGroup !== null) {
      sourcesGroup.subgroups.push(group);
    } else {
      groups.push(group);
    }
    currentGroup = group;
  }

  // Keep full source-section totals when search filters the displayed sheets.
  for (const group of groups) {
    for (const subgroup of group.subgroups) {
      const sizes = subgroup.items.map((item) => item.size_bytes);
      if (sizes.length > 0 && sizes.every((size) => size !== undefined)) {
        subgroup.sizeBytes = sizes.reduce((total, size) => total + size, 0);
      }
    }
    const sourceSizes = group.subgroups.map((subgroup) => subgroup.sizeBytes);
    if (
      group.title?.toLowerCase() === "sources"
      && sourceSizes.length > 0
      && sourceSizes.every((size) => size !== undefined)
    ) {
      group.sizeBytes = sourceSizes.reduce((total, size) => total + size, 0);
    }
  }

  return groups;
}

function sheetNamesByGroup(
  groups: SheetNavigationGroup[],
): Map<string, string[]> {
  const namesByGroup = new Map<string, string[]>();

  const collectNames = (group: SheetNavigationGroup): string[] => {
    const names = [
      ...group.items.map((item) => item.name),
      ...group.subgroups.flatMap(collectNames),
    ];
    namesByGroup.set(group.id, names);
    return names;
  };

  groups.forEach(collectNames);
  return namesByGroup;
}

function filterSheetGroups(
  groups: SheetNavigationGroup[],
  query: string,
): SheetNavigationGroup[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (normalizedQuery.length === 0) return groups;

  return groups.flatMap((group) => {
    const items = group.items.filter((item) =>
      item.name.toLowerCase().includes(normalizedQuery)
    );
    const subgroups = filterSheetGroups(group.subgroups, normalizedQuery);
    if (items.length === 0 && subgroups.length === 0) return [];
    return [{ ...group, items, subgroups }];
  });
}

function groupPathForSheet(
  groups: SheetNavigationGroup[],
  sheetName: string | null,
): string[] {
  if (sheetName === null) return [];

  for (const group of groups) {
    if (group.items.some((item) => item.name === sheetName)) {
      return [group.id];
    }
    const subgroupPath = groupPathForSheet(group.subgroups, sheetName);
    if (subgroupPath.length > 0) return [group.id, ...subgroupPath];
  }
  return [];
}

export function DatabookGenerationStage({
  toolbarActions,
  insights,
  projectId,
  previewRevision = 0,
  versionId,
  onRetryWorkbook,
  state,
  versionOutcome,
  sheetNames,
  sheetItems,
  alertSheetNames,
  sheetListError,
  selectedSheetName,
  sheetPreview,
  sheetPreviewLoading,
  sheetPreviewFetching,
  sheetPreviewError,
  selection = null,
  canEdit,
  canRetry = canEdit,
  onRetry,
  onBack,
  onSelectSheet,
  onSelectSheetPage,
  onToggleOutlineGroup,
  onToggleColumnOutlineGroup = ignoreOutlineToggle,
  onRetrySheet,
  onSelectionChange = ignoreSelectionChange,
  onDeletionApplied,
}: DatabookGenerationStageProps) {
  const [sheetQuery, setSheetQuery] = useState("");
  const inspectorContext = JSON.stringify([projectId, versionId, previewRevision, state.status]);
  const [openInspector, setOpenInspector] = useState<{
    context: string; kind: "variances" | "qna";
  } | null>(null);
  const variancesOpen = openInspector?.context === inspectorContext
    && openInspector.kind === "variances";
  const qnaOpen = openInspector?.context === inspectorContext
    && openInspector.kind === "qna";
  const inspectorOpen = variancesOpen || qnaOpen;
  useEffect(() => {
    setOpenInspector(null);
  }, [inspectorContext]);
  const toolbarButtonClass = "inline-flex shrink-0 items-center gap-2 rounded-lg border border-border px-3 py-2 font-medium text-foreground/80 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50";
  const [deletionImpact, setDeletionImpact] =
    useState<DatabookSheetDeletionImpact | null>(null);
  const [deletionPending, setDeletionPending] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const deletionAvailable = canEdit && projectId !== undefined;
  const groupedSheetItems = useMemo(
    () => groupSheetItems(sheetItems ?? []),
    [sheetItems],
  );
  const deletionNamesByGroup = useMemo(
    () => sheetNamesByGroup(groupedSheetItems),
    [groupedSheetItems],
  );
  const [expandedGroupIds, setExpandedGroupIds] = useState<Set<string>>(
    () => new Set(groupPathForSheet(groupSheetItems(sheetItems ?? []), selectedSheetName)),
  );
  const filteredSheetGroups = useMemo(
    () => filterSheetGroups(groupedSheetItems, sheetQuery),
    [groupedSheetItems, sheetQuery],
  );
  const alertSheets = useMemo(
    () => new Set(alertSheetNames),
    [alertSheetNames],
  );
  const rowOffset = sheetPreview?.row_offset ?? 0;
  const returnedRowCount = sheetPreview?.returned_row_count
    ?? sheetPreview?.row_count
    ?? 0;
  const hasPreviousPage = rowOffset > 0;
  const hasNextPage = rowOffset + returnedRowCount < (sheetPreview?.row_count ?? 0);
  const showPageControls = hasPreviousPage || hasNextPage;

  useEffect(() => {
    const selectedPath = groupPathForSheet(groupedSheetItems, selectedSheetName);
    setExpandedGroupIds((current) => {
      if (selectedPath.every((id) => current.has(id))) return current;
      return new Set([...current, ...selectedPath]);
    });
  }, [groupedSheetItems, selectedSheetName]);

  useEffect(() => {
    setDeletionImpact(null);
    setDeletionError(null);
  }, [projectId, versionId, deletionAvailable]);

  const toggleSheetGroup = (groupId: string) => {
    setExpandedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const analyzeDeletion = async (sheetNames: string[]) => {
    if (!deletionAvailable || !projectId || sheetNames.length === 0 || deletionPending) return;
    setDeletionPending(true);
    setDeletionError(null);
    try {
      setDeletionImpact(
        await analyzeDatabookSheetDeletion(projectId, sheetNames),
      );
    } catch (error) {
      setDeletionError(
        backendErrorMessage(error, "Could not analyze worksheet deletion."),
      );
    } finally {
      setDeletionPending(false);
    }
  };

  const applyDeletion = async () => {
    if (!deletionAvailable || !projectId || deletionImpact === null || deletionPending) return;
    setDeletionPending(true);
    setDeletionError(null);
    try {
      await applyDatabookSheetDeletion(projectId, deletionImpact);
    } catch (error) {
      setDeletionError(
        backendErrorMessage(error, "Could not delete the selected worksheets."),
      );
      if (error instanceof BackendError && error.status === 409) {
        setDeletionImpact(null);
      }
      setDeletionPending(false);
      return;
    }
    setDeletionImpact(null);
    try {
      await onDeletionApplied?.();
    } catch {
      setDeletionError(
        "Worksheets were deleted, but the preview could not be refreshed. Reload the page to see the new version.",
      );
    } finally {
      setDeletionPending(false);
    }
  };

  const displayedOutcome = versionId ? versionOutcome : state.outcome;
  const repeatsCurrentFailure = ["failed", "start_failed"].includes(state.status)
    && displayedOutcome?.error_type === (state.outcome?.error_type ?? "other");

  if (state.status === "ready" || versionId) {
    return (
      <>
      {displayedOutcome?.error_type && !repeatsCurrentFailure && (
        <div role="status" className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/40 px-5 py-2 text-sm">
          <span>{displayedOutcome.error_type === "model_unavailable" ? MODEL_FAILURE_MESSAGE : DATABOOK_FAILURE_MESSAGE}</span>
          {canRetry && displayedOutcome.error_type === "model_unavailable" && displayedOutcome.retry.allowed && <button type="button" onClick={onRetry} className="font-semibold underline">Retry</button>}
        </div>
      )}
      {state.status !== "ready" && (
        <div role="status" className="flex flex-wrap items-center gap-3 border-b border-border bg-muted/40 px-5 py-2 text-sm">
          <span>
            {state.status === "running"
              ? `Rebuilding databook… ${progressPercent(state)}%`
              : state.status === "queued" ? "Rebuild queued…"
              : state.status === "finalizing" ? "Finalizing the new databook…"
              : state.status === "reconnecting" ? "Reconnecting to Databook status…"
              : state.status === "checking" ? "Checking Databook status…"
              : state.status === "failed" || state.status === "start_failed"
                ? (state.outcome?.error_type === "model_unavailable" ? MODEL_FAILURE_MESSAGE : DATABOOK_FAILURE_MESSAGE)
                : "No databook has been built for the current source selection."}
          </span>
          <span className="text-muted-foreground">You can keep viewing saved versions.</span>
          {state.status === "failed" && canRetry && state.outcome?.error_type === "model_unavailable" && state.outcome.retry.allowed && (
            <button type="button" onClick={onRetry} className="font-semibold underline">Retry</button>
          )}
        </div>
      )}
      <ResizablePanelGroup
        direction="horizontal"
        autoSaveId="dilly-databook-sheet-navigation"
        className="min-h-0 flex-1"
      >
        <ResizablePanel defaultSize={15} minSize={10} maxSize={35}>
          <BuilderReportRail fill>
          {sheetListError ? (
            <div
              className="px-2 py-1.5 text-muted-foreground"
              style={{ fontSize: 11.5, lineHeight: 1.5 }}
            >
              Report list could not be loaded.
            </div>
          ) : sheetNames === null ? (
            <div
              className="flex items-center gap-2 px-2 py-1.5 text-muted-foreground"
              style={{ fontSize: 11.5 }}
            >
              <LoaderCircle aria-hidden="true" className="animate-spin" size={12} />
              Loading reports…
            </div>
          ) : sheetNames.length === 0 ? (
            <div
              className="px-2 py-1.5 text-muted-foreground"
              style={{ fontSize: 11.5 }}
            >
              No report sheets found.
            </div>
          ) : (
            <>
              <div className="sticky top-0 z-10 space-y-2 bg-card pb-2">
                <BuilderReportRailSearch
                  value={sheetQuery}
                  onChange={setSheetQuery}
                />
                {deletionError && (
                  <p
                    role="alert"
                    className="rounded-lg border border-destructive/15 bg-destructive/5 px-2.5 py-2 text-destructive"
                    style={{ fontSize: 10.5, lineHeight: 1.4 }}
                  >
                    {deletionError}
                  </p>
                )}
              </div>
              <div>
                {filteredSheetGroups.map((group) => (
                  <SheetNavigationGroupView
                    key={group.id}
                    group={group}
                    nested={false}
                    forceExpanded={sheetQuery.trim().length > 0}
                    expandedGroupIds={expandedGroupIds}
                    selectedSheetName={selectedSheetName}
                    alertSheets={alertSheets}
                    canDelete={deletionAvailable}
                    deletionNamesByGroup={deletionNamesByGroup}
                    deletionPending={deletionPending}
                    onDelete={analyzeDeletion}
                    onToggle={toggleSheetGroup}
                    onSelectSheet={onSelectSheet}
                  />
                ))}
              </div>
            </>
          )}
          </BuilderReportRail>
        </ResizablePanel>

        <ResizableHandle
          aria-label="Resize sheet list"
          title="Drag to resize sheet list"
          className="cursor-col-resize bg-border/60 hover:bg-border"
        />

        <ResizablePanel defaultSize={85} minSize={50} className="min-w-0">
          <div className="flex h-full min-w-0 flex-col bg-muted/50">
          <header className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-card px-5 py-3">
            <div className="min-w-0">
              <div className="truncate font-semibold" style={{ fontSize: 14 }}>
                {selectedSheetName ?? "Databook reports"}
              </div>
              <div className="text-muted-foreground" style={{ fontSize: 11.5 }}>
                Read-only preview
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {toolbarActions}
              <button
                type="button"
                aria-expanded={qnaOpen}
                aria-controls="databook-qna-panel"
                onClick={() => setOpenInspector(qnaOpen ? null : { context: inspectorContext, kind: "qna" })}
                className={toolbarButtonClass}
                style={{ fontSize: 12 }}
              >
                Q&A
              </button>
              <button
                type="button"
                aria-expanded={variancesOpen}
                aria-controls="databook-variance-panel"
                onClick={() => setOpenInspector(variancesOpen ? null : { context: inspectorContext, kind: "variances" })}
                className={toolbarButtonClass}
                style={{ fontSize: 12 }}
              >
                Variances
              </button>
            </div>
          </header>

          <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1">
            <ResizablePanel id="worksheet" order={1} minSize={35} defaultSize={inspectorOpen ? 72 : 100}>
              <div className="h-full min-h-0 p-4">
                <div className="h-full overflow-hidden rounded-lg border border-border bg-white shadow-sm">
                  {sheetPreviewLoading ? (
                    <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                      <LoaderCircle aria-hidden="true" className="animate-spin" size={16} />
                      Loading report…
                    </div>
                  ) : sheetPreviewError ? (
                    <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                      <AlertTriangle aria-hidden="true" className="text-destructive" size={22} />
                      <span>This report could not be loaded.</span>
                      <button
                        type="button"
                        onClick={onRetrySheet}
                        className="rounded-lg border border-border px-3 py-1.5 font-medium text-foreground hover:bg-muted"
                      >
                        Try again
                      </button>
                    </div>
                  ) : sheetPreview ? (
                    <div className="flex h-full min-h-0 flex-col">
                      <div className="min-h-0 flex-1">
                        <DatabookSheetGrid
                          sheet={sheetPreview}
                          outlineLoading={sheetPreviewFetching}
                          selection={selection}
                          onSelectionChange={onSelectionChange}
                          onToggleOutlineGroup={onToggleOutlineGroup}
                          onToggleColumnOutlineGroup={onToggleColumnOutlineGroup}
                        />
                      </div>
                      {showPageControls && (
                        <div className="flex shrink-0 items-center justify-between border-t border-border bg-card px-3 py-2">
                          <span className="text-muted-foreground" style={{ fontSize: 11.5 }}>
                            Rows {rowOffset + 1}–{rowOffset + returnedRowCount} of{" "}
                            {sheetPreview.row_count}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              aria-label="Previous rows"
                              disabled={!hasPreviousPage}
                              onClick={() => onSelectSheetPage(
                                previousDatabookSheetPage(rowOffset),
                              )}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-foreground/80 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                              style={{ fontSize: 11.5 }}
                            >
                              <ChevronLeft aria-hidden="true" size={13} />
                              Previous
                            </button>
                            <button
                              type="button"
                              aria-label="Next rows"
                              disabled={!hasNextPage}
                              onClick={() => onSelectSheetPage(
                                nextDatabookSheetPage(
                                  rowOffset,
                                  returnedRowCount,
                                ),
                              )}
                              className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-foreground/80 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                              style={{ fontSize: 11.5 }}
                            >
                              Next
                              <ChevronRight aria-hidden="true" size={13} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                      Select a report to preview it.
                    </div>
                  )}
                </div>
              </div>

            </ResizablePanel>
            {inspectorOpen && (
              <>
                <ResizableHandle aria-label={qnaOpen ? "Resize Q&A" : "Resize variance explanations"} />
                <ResizablePanel id="report-inspector" order={2} defaultSize={28} minSize={20} maxSize={65}>
                  <DatabookInsightsInspector
                    insights={insights}
                    key={`${inspectorContext}:${openInspector.kind}`}
                    kind={openInspector.kind}
                    projectId={projectId} versionId={versionId} previewRevision={previewRevision}
                    sheetName={selectedSheetName} sheetNames={sheetNames ?? []}
                    sheetListError={sheetListError}
                    onSelectSheet={onSelectSheet}
                    onRetryWorkbook={onRetryWorkbook ?? onRetrySheet}
                    onClose={() => setOpenInspector(null)} />
                </ResizablePanel>
              </>
            )}
          </ResizablePanelGroup>

          <div className="shrink-0 px-4 pb-4">
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-border bg-card px-3 py-2 font-medium text-foreground/80 hover:bg-muted"
              style={{ fontSize: 12 }}
            >
              ← Back
            </button>
          </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
      <AlertDialog
        open={deletionImpact !== null}
        onOpenChange={(open) => {
          if (!open && !deletionPending) setDeletionImpact(null);
        }}
      >
        <AlertDialogContent className="max-w-md gap-0 overflow-hidden rounded-2xl p-0">
          <AlertDialogHeader className="space-y-4 p-6 pb-5 text-left">
            <AlertDialogTitle className="text-base">
              Delete {deletionImpact?.selected_sheet_names.length ?? 0}{" "}
              {(deletionImpact?.selected_sheet_names.length ?? 0) === 1
                ? "worksheet"
                : "worksheets"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <div className="rounded-xl border border-border/70 bg-muted/25 p-3">
                  <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                    {deletionImpact?.selected_sheet_names.map((sheetName) => (
                      <span
                        key={sheetName}
                        className="max-w-full truncate rounded-md border border-border/70 bg-background px-2 py-1 text-xs font-medium text-foreground"
                        title={sheetName}
                      >
                        {sheetName}
                      </span>
                    ))}
                  </div>
                </div>
                {deletionImpact?.dependent_sheet_names.length ? (
                  <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] p-3">
                    <div className="flex gap-2.5">
                      <AlertTriangle
                        aria-hidden="true"
                        className="mt-0.5 shrink-0 text-amber-600"
                        size={15}
                      />
                      <div className="min-w-0">
                        <div className="text-xs font-semibold text-foreground">
                          Dependent formulas will be hardcoded
                        </div>
                        <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
                          References to the deleted worksheets will keep their
                          currently stored values in:
                        </p>
                        <div className="mt-2 flex max-h-20 flex-wrap gap-1.5 overflow-y-auto">
                          {deletionImpact.dependent_sheet_names.map((sheetName) => (
                            <span
                              key={sheetName}
                              className="max-w-full truncate rounded-md bg-amber-500/10 px-2 py-1 text-xs font-medium text-foreground"
                              title={sheetName}
                            >
                              {sheetName}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-border/70 px-3 py-2.5 text-xs text-muted-foreground">
                    No other worksheets contain direct references to this
                    selection.
                  </div>
                )}
                {deletionError && (
                  <p
                    role="alert"
                    className="rounded-lg border border-destructive/15 bg-destructive/5 px-3 py-2 text-xs text-destructive"
                  >
                    {deletionError}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="border-t border-border/70 bg-muted/20 px-6 py-4">
            <AlertDialogCancel
              disabled={deletionPending}
              className="rounded-lg"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deletionPending}
              onClick={(event) => {
                event.preventDefault();
                void applyDeletion();
              }}
              className="rounded-lg bg-foreground text-background shadow-sm hover:bg-foreground/90"
            >
              {deletionPending ? (
                <>
                  <LoaderCircle aria-hidden="true" className="animate-spin" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 aria-hidden="true" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
    );
  }

  const progress = progressPercent(state);
  const working = [
    "checking",
    "reconnecting",
    "finalizing",
    "queued",
    "running",
  ].includes(state.status);
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[880px] px-8 pt-14 pb-20">
        <div className="mb-6 font-semibold" style={{ fontSize: 20 }}>
          Databook
        </div>

        <section className="rounded-xl border border-border bg-card p-6">
          {state.status === "not_started" && (
            <div className="text-center">
              <FileSpreadsheet
                aria-hidden="true"
                className="mx-auto mb-3 text-muted-foreground"
                size={28}
              />
              <div className="font-semibold" style={{ fontSize: 14 }}>
                Choose a Basis before building
              </div>
              <div className="mt-1 text-muted-foreground" style={{ fontSize: 12 }}>
                Return to Basis of preparation and start the databook.
              </div>
            </div>
          )}

          {working && (
            <div>
              <div className="flex items-center gap-3">
                <LoaderCircle
                  aria-hidden="true"
                  className="animate-spin text-muted-foreground"
                  size={22}
                />
                <div className="font-semibold" style={{ fontSize: 14 }}>
                  {state.status === "checking"
                    ? "Checking Databook status"
                    : state.status === "reconnecting"
                      ? "Reconnecting to Databook status"
                    : state.status === "finalizing"
                    ? "Preparing the selected Basis"
                    : state.status === "queued"
                      ? "Databook queued"
                      : "Building the databook"}
                </div>
              </div>
              {state.status === "running" && (
                <div className="mt-5">
                  <div className="mb-1.5 flex justify-between text-muted-foreground">
                    <span style={{ fontSize: 10.5 }}>
                      {state.completedSteps} of {state.totalSteps} steps
                    </span>
                    <span style={{ fontSize: 10.5 }}>{progress}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-foreground transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {(state.status === "failed" || state.status === "start_failed") && (
            <div className="text-center">
              <AlertTriangle
                aria-hidden="true"
                className="mx-auto mb-3 text-destructive"
                size={28}
              />
              <div className="font-semibold text-destructive" style={{ fontSize: 14 }}>
                Something went wrong
              </div>
              <div
                className="mx-auto mt-1 max-w-lg text-muted-foreground"
                style={{ fontSize: 12, lineHeight: 1.5 }}
              >
                {state.outcome?.error_type === "model_unavailable" ? MODEL_FAILURE_MESSAGE : "Please allow up to 24 hours for your databook to be ready."}
              </div>
              {canRetry && state.outcome?.error_type === "model_unavailable" && state.outcome.retry.allowed && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-4 rounded-lg bg-foreground px-4 py-2 font-medium text-background hover:opacity-90"
                  style={{ fontSize: 12.5 }}
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </section>

        <button
          type="button"
          onClick={onBack}
          className="mt-6 rounded-lg border border-border px-4 py-2 font-medium text-foreground/80 hover:bg-muted"
          style={{ fontSize: 12.5 }}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

function SheetDeletionContextMenu({
  children,
  sheetNames,
  disabled,
  onDelete,
}: {
  children: ReactElement;
  sheetNames: string[];
  disabled: boolean;
  onDelete: (sheetNames: string[]) => void;
}) {
  if (disabled || sheetNames.length === 0) return children;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-40">
        <ContextMenuItem className="gap-2" onSelect={() => onDelete(sheetNames)}>
          <Trash2 aria-hidden="true" size={14} />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

function SheetNavigationGroupView({
  group,
  nested,
  forceExpanded,
  expandedGroupIds,
  selectedSheetName,
  alertSheets,
  canDelete,
  deletionNamesByGroup,
  deletionPending,
  onDelete,
  onToggle,
  onSelectSheet,
}: {
  group: SheetNavigationGroup;
  nested: boolean;
  forceExpanded: boolean;
  expandedGroupIds: Set<string>;
  selectedSheetName: string | null;
  alertSheets: Set<string>;
  canDelete: boolean;
  deletionNamesByGroup: Map<string, string[]>;
  deletionPending: boolean;
  onDelete: (sheetNames: string[]) => void;
  onToggle: (groupId: string) => void;
  onSelectSheet: (sheetName: string) => void;
}) {
  if (group.title === null) {
    return (
      <SheetNavigationItems
        items={group.items}
        selectedSheetName={selectedSheetName}
        alertSheets={alertSheets}
        canDelete={canDelete}
        deletionPending={deletionPending}
        onDelete={onDelete}
        onSelectSheet={onSelectSheet}
      />
    );
  }

  const expanded = forceExpanded || expandedGroupIds.has(group.id);
  const groupSheetNames = deletionNamesByGroup.get(group.id) ?? [];
  return (
    <div
      role="group"
      aria-label={`${group.title}${nested ? " source" : ""} sheets`}
      className={nested
        ? "py-0.5"
        : group.title.toLowerCase() === "sources"
          ? "pt-2 mt-2 border-t border-border/60"
          : ""}
    >
      <SheetDeletionContextMenu
        sheetNames={groupSheetNames}
        disabled={!canDelete || deletionPending}
        onDelete={onDelete}
      >
        <div
          className={`group flex w-full items-center gap-1 rounded-md px-1.5 py-1 text-left text-muted-foreground/70 hover:bg-muted/50 hover:text-foreground ${
            nested
              ? "font-medium"
              : "mt-2 uppercase tracking-wider"
          }`}
          style={{ fontSize: nested ? 10 : 9 }}
        >
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => onToggle(group.id)}
            className="flex min-w-0 flex-1 items-center gap-1 text-left"
          >
            <ChevronRight
              aria-hidden="true"
              size={nested ? 11 : 10}
              className={`shrink-0 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
            <span className="truncate">{group.title}</span>
            {group.sizeBytes !== undefined && (
              <span className="ml-auto shrink-0 tabular-nums text-[10px] font-normal text-muted-foreground/70">
                {formatSheetSize(group.sizeBytes)}
              </span>
            )}
          </button>
        </div>
      </SheetDeletionContextMenu>
      {expanded && (
        <div className={`${nested ? "ml-2 pl-1" : "pl-2"} border-l border-border/60`}>
          <SheetNavigationItems
            items={group.items}
            selectedSheetName={selectedSheetName}
            alertSheets={alertSheets}
            canDelete={canDelete}
            deletionPending={deletionPending}
            onDelete={onDelete}
            onSelectSheet={onSelectSheet}
          />
          {group.subgroups.map((subgroup) => (
            <SheetNavigationGroupView
              key={subgroup.id}
              group={subgroup}
              nested
              forceExpanded={forceExpanded}
              expandedGroupIds={expandedGroupIds}
              selectedSheetName={selectedSheetName}
              alertSheets={alertSheets}
              canDelete={canDelete}
              deletionNamesByGroup={deletionNamesByGroup}
              deletionPending={deletionPending}
              onDelete={onDelete}
              onToggle={onToggle}
              onSelectSheet={onSelectSheet}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SheetNavigationItems({
  items,
  selectedSheetName,
  alertSheets,
  canDelete,
  deletionPending,
  onDelete,
  onSelectSheet,
}: {
  items: DatabookSheetListItem[];
  selectedSheetName: string | null;
  alertSheets: Set<string>;
  canDelete: boolean;
  deletionPending: boolean;
  onDelete: (sheetNames: string[]) => void;
  onSelectSheet: (sheetName: string) => void;
}) {
  return items.map((item) => (
    <SheetDeletionContextMenu
      key={item.name}
      sheetNames={[item.name]}
      disabled={!canDelete || deletionPending}
      onDelete={onDelete}
    >
      <div>
        <BuilderReportRailItem
          active={item.name === selectedSheetName}
          role="button"
          tabIndex={0}
          title={item.name}
          onClick={() => onSelectSheet(item.name)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onSelectSheet(item.name);
            }
          }}
        >
          <span
            className={`flex-1 truncate ${
              alertSheets.has(item.name) ? "text-destructive" : ""
            }`}
            title={alertSheets.has(item.name) ? "Variance check failed" : undefined}
          >
            {item.name}
          </span>
          {item.size_bytes !== undefined && (
            <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground/70">
              {formatSheetSize(item.size_bytes)}
            </span>
          )}
        </BuilderReportRailItem>
      </div>
    </SheetDeletionContextMenu>
  ));
}
